import React, { Fragment, memo, useCallback, useEffect, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { ComposedInvoicesTable } from 'src/components/invoice/composed-invoices-table/ComposedInvoicesTable'
import { ComposedInvoicesTableToolbar } from 'src/components/invoice/composed-invoices-table/ComposedInvoicesTableToolbar'
import { DueInterestCard } from 'src/components/invoice/due-interest/DueInterestCard'
import { InvoiceActionModal } from 'src/components/invoice/action-modal/InvoiceActionModal'
import {
  AdminClearInvoiceActionRun,
  AdminFetchInvoiceDueInterest,
  AdminFetchInvoices,
  AdminRunInvoiceAction,
} from 'src/constants/actionTypes'
import { invoicePageSizeOptions } from 'src/constants/invoice/constants'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { InvoiceActionResult, PendingInvoiceAction } from 'src/interfaces/invoice'
import { getInvoiceActionRun, getInvoicesError } from 'src/selectors/invoice'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'

const CustomerInvoicePageComponent = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const dispatch = useDispatch()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const { isAuthenticated } = useAuthSession()

  const { paginationModel, limit, offset, setPaginationModel } =
    useGridPagination(invoicePageSizeOptions)

  // Two independent counters: a batch bumps both, but the card's own Refresh must not re-run
  // the invoice list — that mis-wiring is exactly what the prototype set out to fix.
  const [refreshToken, setRefreshToken] = useState<number>(0)
  const [dueInterestRefreshToken, setDueInterestRefreshToken] = useState<number>(0)
  const [pendingAction, setPendingAction] = useState<PendingInvoiceAction | null>(null)

  const invoicesError = useSelector(getInvoicesError)
  const actionRun = useSelector(getInvoiceActionRun)
  // Every invoice action posts to an `invoices_manage` route; without it the row menu is gone.
  const canManageInvoices = useHasPermission(InternalPermission.InvoicesManage)

  const handleRefreshInvoices = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  const handleRefreshDueInterest = useCallback(() => {
    setDueInterestRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  useEffect(() => {
    if (!isAuthenticated || !customerId) {
      return
    }

    asyncDispatchWithErrorToast(
      AdminFetchInvoices.request({ partnerId: customerId, limit, offset }),
    )
  }, [asyncDispatchWithErrorToast, isAuthenticated, customerId, limit, offset, refreshToken])

  useEffect(() => {
    if (!isAuthenticated || !customerId) {
      return
    }

    // Silent: DueInterestCard reports its own failure inline, so the grid beside it stays
    // usable when only this call fails — no need to double it up with a toast.
    asyncDispatchWithErrorToast(
      AdminFetchInvoiceDueInterest.request({ partnerId: customerId }),
      true,
    )
  }, [asyncDispatchWithErrorToast, isAuthenticated, customerId, dueInterestRefreshToken])

  const handleRunAction = useCallback((action: PendingInvoiceAction) => {
    setPendingAction(action)
  }, [])

  /**
   * The only place a money action is dispatched. Resolves with the per-invoice results even
   * when some failed (the saga never rejects for a partial failure), so there is no toast here
   * — the modal's results step reports the outcome.
   */
  const handleConfirmAction = useCallback(
    async (invoiceIds: string[]) => {
      if (!pendingAction || !customerId) {
        return
      }

      await asyncDispatchWithErrorToast<InvoiceActionResult[]>(
        AdminRunInvoiceAction.request({
          partnerId: customerId,
          invoiceIds,
          verb: pendingAction.verb,
          composedInvoiceLabel: pendingAction.composedInvoiceLabel,
          invoiceLabels: pendingAction.invoiceLabels,
        }),
      )
    },
    [pendingAction, customerId, asyncDispatchWithErrorToast],
  )

  /**
   * Every POST returns void and the cascading loan/payment state cannot be reconstructed
   * client-side, so both reads are refetched rather than patched — the server stays the single
   * source of truth. Due interest moves too, after a repay-due-interest run.
   */
  const handleCloseModal = useCallback(() => {
    // Gated on a run having actually happened, not merely on the modal being open — dismissing
    // the confirm step changes nothing, so it must not trigger two refetches.
    const hadRun = actionRun !== null

    setPendingAction(null)
    dispatch(AdminClearInvoiceActionRun())

    if (!hadRun) {
      return
    }

    handleRefreshInvoices()
    handleRefreshDueInterest()
  }, [actionRun, dispatch, handleRefreshInvoices, handleRefreshDueInterest])

  return (
    <Fragment>
      <div data-testid="admin-customer-invoice-page" css={tw`min-w-0 flex-1 p-6`}>
        <div css={tw`mb-5`}>
          <Typography variant="h2">Invoice</Typography>
        </div>

        <DueInterestCard onRefresh={handleRefreshDueInterest} />

        {invoicesError ? (
          <Typography variant="body2" css={tw`mb-3 text-critical-500`}>
            {invoicesError}
          </Typography>
        ) : null}

        <ComposedInvoicesTableToolbar onRefresh={handleRefreshInvoices} />
        <ComposedInvoicesTable
          paginationModel={paginationModel}
          canManage={canManageInvoices}
          onPaginationModelChange={setPaginationModel}
          onRunAction={handleRunAction}
        />
      </div>
      <InvoiceActionModal
        pendingAction={pendingAction}
        onConfirm={handleConfirmAction}
        onClose={handleCloseModal}
      />
    </Fragment>
  )
}

export const CustomerInvoicePage = memo(CustomerInvoicePageComponent)
